import React, { useEffect, useState } from 'react';
import CommonModal from '@/components/common/CommonModal';
import { Minus, Plus } from 'lucide-react';
import Button from '@/components/common/Button';
import { IAddon } from '@/interfaces/Addon/addon.interface';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import FormField from '@/components/common/FormField';

interface UpdateAddonStockModalProps {
    isOpen: boolean;
    onClose: () => void;
    addon: IAddon | null;
    onSuccess: (updatedAddonId: string, newStock: number) => void;
}

export const UpdateAddonStockModal: React.FC<UpdateAddonStockModalProps> = ({
    isOpen,
    onClose,
    addon,
    onSuccess
}) => {
    const [addQuantity, setAddQuantity] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!isOpen) {
            setAddQuantity(null);
        }
    }, [isOpen]);

    if (!isOpen || !addon) return null;

    // Use whichever key resolves first from the interface based on standard DB implementations
    const currentStock = addon.inventory ?? addon.quantity ?? 0;

    const handleUpdateStock = async () => {
        if (!addQuantity) {
            showToastNotification('danger', `Please enter quantity to add`);
            return;
        };

        const newTotalStock = addQuantity;
        setLoading(true);
        try {
            await callApi(`${API_URL}${API_ENDPOINT.addons}/${addon.id}`, {
                method: 'PATCH',
                data: { inventory: newTotalStock }
            });

            showToastNotification('success', `Inventory updated successfully for ${addon.name}`);
            onSuccess(addon.id, newTotalStock);
            onClose();
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };


    return (
        <CommonModal open={isOpen} onOpenChange={onClose} title={addon.name} size="lg">
            {/* Quantity Present */}
            <div className="mb-6 mt-2 flex justify-between items-center bg-primary/5 rounded-2xl px-5 py-4 border border-primary/10">
                <span className="text-foreground font-semibold text-[15px]">Quantity Present</span>
                <span className="text-foreground font-bold text-lg">{currentStock}</span>
            </div>

            {/* Add Stock Control */}
            <div className="mb-8 p-1">
                <span className="text-foreground font-bold text-sm mb-4 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Enter Quantity to Add
                </span>
                <div className="flex items-center gap-4 bg-secondary/5 p-4 rounded-3xl border border-border/50 group focus-within:border-primary/30 transition-all duration-300 shadow-inner">
                    <FormField
                        type="number"
                        name="addQuantity"
                        value={addQuantity ?? ''}
                        onChange={(e: any) => {
                            const val = e.target.value.replace(/[^0-9]/g, "");

                            if (val === '') {
                                setAddQuantity(null);
                                return;
                            }

                            setAddQuantity(val);
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        wrapperClass="w-full mb-0"
                        inputClass="w-full bg-transparent text-center text-foreground font-black text-4xl focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-muted-foreground/20 border-none ring-0 focus:ring-0 shadow-none h-14"
                        placeholder="0"
                        disabled={loading}
                        autoFocus
                    />
                </div>
                <p className="text-[10px] text-muted-foreground mt-3 text-center font-medium">
                    The new total stock will be <span className="text-primary font-bold">{addQuantity}</span> units.
                </p>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
                <Button
                    variant="outline"
                    onClick={onClose}
                    className="w-full rounded-2xl h-12 border border-border hover:bg-secondary transition-colors font-bold text-[15px]"
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleUpdateStock}
                    isLoading={loading}
                    className="w-full rounded-2xl h-12 bg-amber-500 hover:bg-amber-600 text-white font-bold text-[15px]"
                >
                    Add Quantity
                </Button>
            </div>
        </CommonModal>
    );
};
